import { isEmpty, isNil } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { BatchEditContextProvider } from '@shared/scenes/cards/entityBulkActions/BatchCreateContext';
import { CardFormTypeContext } from '@shared/scenes/entity/cardFormTypeContext';
import { EntityForm } from '@shared/scenes/entity/components/EntityForm';
import { CardSummaryProvider } from '@shared/scenes/entity/components/EntityForm/CardSummaryProvider';
import { FormErrors } from '@shared/scenes/entity/components/FormErrors';
import { RemoteChangesWarningBanner } from '@shared/scenes/entity/components/RemoteChangesWarningBanner';
import { SubformNavigationContext } from '@shared/scenes/entity/components/fields/Subform/SubformEditField';
import { FieldConflictContext } from '@shared/scenes/entity/context';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import classNames from './CardEntityEditForm.module.css';

interface Props {
  fields: FieldRecord | null;
  updateField: UpdateField;
  components: API.AnyComponent[];
  errors: CardEntityError[];
  workflowTemplateId?: string | null;
  viewTemplateId?: string | null;
  entityId: string;
  saveAttempted?: boolean;
  conflictingComponentIds?: string[];
  revertConflicts?: () => void;
  showRemoteChangesWarning?: boolean;
}

/**
 * The edit form's content: banners plus the fields. Scrolling is owned by the
 * container that renders this (the card modal's FormWithChat, the external
 * card page), so the form always lives inside exactly one scroller.
 */
export function CardEntityEditForm({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  viewTemplateId,
  entityId,
  saveAttempted,
  conflictingComponentIds = [],
  revertConflicts,
  showRemoteChangesWarning: externalShowWarning,
}: Props) {
  const [isConflictBannerDismissed, setIsConflictBannerDismissed] = useState(false);
  const prevConflictCountRef = useRef(0);
  useEffect(() => {
    const currentCount = conflictingComponentIds.length;
    if (currentCount > prevConflictCountRef.current) {
      setIsConflictBannerDismissed(false);
    }
    prevConflictCountRef.current = currentCount;
  }, [conflictingComponentIds.length]);

  const showRemoteChangesWarning =
    externalShowWarning ?? (!isEmpty(conflictingComponentIds) && !isConflictBannerDismissed);

  if (isNil(fields) || isNil(workflowTemplateId) || isNil(viewTemplateId)) {
    return null;
  }

  return (
    <FieldConflictContext.Provider value={conflictingComponentIds}>
      <CardSummaryProvider>
        <CardFormTypeContext.Provider value="EDIT">
          <BatchEditContextProvider>
            <SubformNavigationContext
              fields={fields}
              updateField={updateField}
              saveAttempted={saveAttempted}
              apiErrors={errors}
              entityId={entityId}
            >
              <div className={classNames.stickyBanners}>
                <FormErrors errors={errors} />
                {showRemoteChangesWarning && (
                  <RemoteChangesWarningBanner
                    header={i18n('card.update.remoteConflictHeader')}
                    subheader={i18n('card.update.remoteConflictSubheader')}
                    onDismiss={() => setIsConflictBannerDismissed(true)}
                    onRevert={revertConflicts}
                  />
                )}
              </div>
              <EntityForm
                fields={fields}
                updateField={updateField}
                components={components}
                errors={errors}
                workflowTemplateId={workflowTemplateId}
                viewTemplateId={viewTemplateId}
                entityId={entityId}
              />
            </SubformNavigationContext>
          </BatchEditContextProvider>
        </CardFormTypeContext.Provider>
      </CardSummaryProvider>
    </FieldConflictContext.Provider>
  );
}
